
import { ResourceTabType, UserResourceType } from '@/interface/library';
import { ISpineResult } from '@/material/form-upload/folder';
import { transformObjectDeep } from '@/utils/tans/object';

import { apiPathServer } from './api-config';
import { CgiErrorCode, CgiResp } from './cgi';
import { API_PREFIX, request } from './request';

export interface GetListReq {
  tabType: ResourceTabType;
  type?: UserResourceType;
  materialId?: string;
  resourceName?: string;
  page?: number;
  size?: number
}
export interface StoryUserResourceDetail {
  sceneResourceList: SceneUserResourceDetail[];
  storyId: string;
  storyName: string;
}

export interface ListResp {
  storyResourceList: StoryUserResourceDetail[];
}

export interface GetListResp {
  userResourceList: {
    type: UserResourceType;
    resourceName: string;
    url: string;
    resourceId: string;
    spine?: ISpineResult
  }[];
  pageinfo: {
    page: number;
    size: number;
    total: number;
  }
}
export interface MaterialUserResourceDetail {
  subMaterialResourceList: SubMaterialResourceList[];
  materialId: string;
  materialName: string;
}

export interface SubMaterialResourceList {
  userResourceList: UserResourceDetail[];
  subMaterialId: string;
  subMaterialName: string;
}

export interface SceneUserResourceDetail {
  userResourceList: UserResourceDetail[];
  sceneId: string;
  sceneName: string
}

export interface SkinUserResourceDetail {
  userResourceList: UserResourceDetail[];
  skinId: string;
  skinName: string
}
export interface UserResourceDetail {
  type?: UserResourceType;
  resourceName?: string;
  url?: string;
  createTime?: string;
  resourceId?: string;
  spine?: ISpineResult;
  keyIndex?: string
}

export interface GetInsertResp {
  url: string;
}

export interface DeleteUserResourceBelongReq {
  resourceIds: string[];
  materialId?: string;
  tabType?: ResourceTabType
}

const defaultPageInfo = {
  total: 0,
  page: 1,
  size: 10,
};

export const matchResourceList = async (params: {
  tabType: ResourceTabType;
  materialId?: string;
  batchMatch: {
    type: UserResourceType,
    resourceName: string
  }[]
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request<CgiResp<GetListResp>>(`${API_PREFIX}${apiPathServer.ListUserResourceMatch}`, {
    method: 'POST',
    body: { ...postData },
  });
  const postRes = transformObjectDeep({
    ...res,
  }, 'camel', true) as CgiResp<GetListResp>;
  try {
    const {
      userResourceList = [],
      pageinfo: pageInfo = {
        ...defaultPageInfo,
      } } = postRes?.data || {};

    return {
      code: postRes?.code,
      message: postRes?.message,
      userResourceList,
      pageInfo: {
        total: pageInfo?.total,
        current: pageInfo?.page,
        pageSize: pageInfo?.size,
      },
    };
  } catch (error) {
    return {
      code: CgiErrorCode.ValidateError,
      userResourceList: [],
      pageInfo: { ...defaultPageInfo },
    };
  }
};


export const getResourceList = async (params: GetListReq) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request<CgiResp<GetListResp>>(`${API_PREFIX}${apiPathServer.ListUserResourceV2}`, {
    method: 'POST',
    body: { ...postData },
  });
  const postRes = transformObjectDeep({
    ...res,
  }, 'camel', true) as CgiResp<GetListResp>;
  try {
    const {
      userResourceList = [],
      pageinfo: pageInfo = {
        ...defaultPageInfo,
      } } = postRes?.data || {};

    return {
      code: postRes?.code,
      message: postRes?.message,
      userResourceList,
      pageInfo: {
        total: pageInfo?.total,
        current: pageInfo?.page,
        pageSize: pageInfo?.size,
      },
    };
  } catch (error) {
    return {
      code: CgiErrorCode.ValidateError,
      userResourceList: [],
      pageInfo: { ...defaultPageInfo },
    };
  }
};

export const insertLibrary = async (params: {
  type?: UserResourceType,
  materialId?: string,
  batchResource?: { url: string, resourceName: string, spine?: ISpineResult }[]
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.InsertUserResource}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
    list: GetListResp['userResourceList']
  }>;
};

export const deleteLibrary = async (params: DeleteUserResourceBelongReq) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.DeleteUserResourceBelong}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<GetInsertResp>;
};

export const updateUserResource = async (params: {
  resourceId: string;
  name: string;
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.updateUserResource}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<GetInsertResp>;
};

export const getLibraryGroupList = async (params: {
  materialId: string,
  tabType: ResourceTabType,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.ListUserResourceCustomGroupReq}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
    groupList: {
      id: string;
      name: string;
      disableOperate: boolean;
    }[];
  }>;
};

export const moveGroup = async (params: {
  resourceIds: string[],
  toId: string,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.MoveGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
  }>;
};

export const setLastViewGroup = async (params: {
  lastViewGroup: {
    id: string;
    tabType: string;
    materialId?: string;
  },
  storyId: string,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.SetLastViewGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
  }>;
};

export const getLastViewGroup = async (params: {

  storyId: string,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.GetLastViewGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
    lastViewGroup: {
      id: string;
      tabType: ResourceTabType;
      materialId?: string;
    },
  }>;
};

export const deleteUserResourceCustomGroup = async (params: {
  ids: string[]
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.DeleteUserResourceCustomGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<GetInsertResp>;
};

export const insertLibraryGroup = async (params: {
  name: string,
  materialId: string,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.InsertCustomGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
    id: string
  }>;
};

export const updateLibraryGroup = async (params: {
  name: string,
  id: string,
}) => {
  const postData = transformObjectDeep({
    ...params,
  }, 'snake') || {};
  const res = await request(`${API_PREFIX}${apiPathServer.UpdateCustomGroup}`, {
    method: 'POST',
    body: { ...postData },
  });

  return transformObjectDeep({
    ...res,
  }, 'camel') as CgiResp<{
    id: string
  }>;
};

